<template>
    <meta-data
        page="shop-accessories-details"
        :title="accessory?.brand + ' ' + accessory?.model"
        :description="accessory?.description"
        :path="$route.fullPath"
        ogType="website"
        :ogImage="
            '/storage/assets/images/accessories_pictures/' +
            accessory?.id +
            '/A.jpg'
        "
        :isExternalApi="true"
    />
    <InfoBar :text="$translate('components.info-bar.text')" />
    <Header logo="/storage/assets/logos/rectangle_blanc.png" />
    <div class="text-black mt-2 mb-2 w-full md:w-3/4 mx-auto">
        <RouterLink :to="{ name: 'home' }" class="text-black p-4">
            {{ $translate('public.shop.home.bikes.home') }}
        </RouterLink>
        <i class="fa-solid fa-chevron-right fa-2xs"></i>
        <RouterLink :to="{ name: 'home-shop' }" class="text-black p-4">
            {{ $translate('public.shop.home.bikes.shop') }}
        </RouterLink>
        <i class="fa-solid fa-chevron-right fa-2xs"></i>
        <RouterLink
            :to="{ name: 'accessories-catalog-categories' }"
            class="text-black p-4"
        >
            {{ $translate('public.shop.home.accessories.catalog') }}
        </RouterLink>
        <i class="fa-solid fa-chevron-right fa-2xs"></i>
        <h1 class="inline text-black ml-4 text-base">
            {{ accessory?.brand }} {{ accessory?.model }}
        </h1>
    </div>
    <div class="w-full bg-red text-white font-bold text-center p-4">
        <p>
            {{ $translate('public.shop.home.accessories.details.services') }}
        </p>
    </div>

    <div class="w-full md:w-3/4 mx-auto mt-4 grid grid-cols-1 md:grid-cols-2">
        <div>
            <img
                class="img remove_bg_color"
                :src="
                    $apiUrl() +
                    '/storage/assets/images/accessories_pictures/' +
                    accessory?.id +
                    '/A.jpg'
                "
                :alt="'Image de ' + accessory?.brand + ' ' + accessory?.model"
                @error="
                    e => (e.target.src = '/storage/assets/images/default.png')
                "
            />
        </div>
        <div>
            <div class="justify-center text-center">
                <div class="flex text-center justify-center">
                    <strong class="text-xl font-bold text-red">
                        {{ accessory.brand + ' ' + accessory.model }}
                    </strong>
                    <div
                        @click="
                            addToFav(accessory.id);
                            accessory.favorite = !accessory.favorite;
                        "
                        class="mt-1 ml-2"
                    >
                        <i
                            class="text-red align-right"
                            :class="
                                accessory.favorite === true
                                    ? 'fa fa-heart fa-xl'
                                    : 'fa-regular fa-heart fa-xl'
                            "
                        ></i>
                    </div>
                </div>
                <div class="align-items-end w-100 gap-2 mb-2">
                    <div v-if="accessory.discount != 0" class="promo-container">
                        <del class="del-price">
                            {{ formatPrice(accessory?.price_tvac) }}
                        </del>
                        <div class="text-xl font-bold text-black">
                            {{
                                formatPrice(
                                    accessory?.price_tvac -
                                        (accessory?.price_tvac *
                                            accessory.discount) /
                                            100,
                                )
                            }}
                        </div>
                    </div>
                    <p class="text-xl font-bold text-black" v-else>
                        {{ accessory?.price_tvac }} €
                    </p>
                </div>
                <div v-if="accessory.discount" class="w-full text-red">
                    <i class="fa-solid fa-circle-exclamation"></i>
                    <strong class="ml-4">{{
                        $translate(
                            'public.shop.home.bikes.card.details.discount.explanation',
                        )
                    }}</strong>
                </div>

                <div class="description mt-4 text-left">
                    <h2 class="font-bold text-xl mb-2">
                        {{
                            $translate(
                                'public.shop.home.accessories.details.description-title',
                            )
                        }}
                    </h2>
                    <p
                        class="text"
                        v-if="accessory?.description"
                        v-html="accessory?.description"
                    ></p>
                    <p class="text" v-if="!accessory?.description">
                        {{
                            $translate(
                                'public.shop.home.accessories.details.description-empty',
                            )
                        }}
                    </p>
                </div>

                <div class="mt-4">
                    <Sizes
                        type="accessory"
                        :item="accessory"
                        :selectedSize="selectedSize"
                        @updateSize="selectedSize = $event"
                    />
                </div>
            </div>
        </div>
    </div>

    <div class="text-center mt-4 font-bold">
        <p>
            {{ $translate('public.shop.home.accessories.details.infos') }}
        </p>
        <p>
            {{ $translate('public.shop.home.accessories.details.contact-1') }}
            <a href="tel:+32 4 290 71 21" class="text-red">+32 4 290 71 21</a>
            {{ $translate('public.shop.home.accessories.details.contact-2') }}
            <a href="mailto:info@kameobikes.com" class="text-red"
                >info@kameobikes.com</a
            >
        </p>
    </div>
    <Contact :defaultInfo="userInfos" />

    <Footer />
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/js/stores/authStore.js';

import Footer from '@/js/components/public/Footer.vue';
import Header from '@/js/components/public/Header.vue';
import InfoBar from '@/js/components/public/InfoBar.vue';
import { useApiStore } from '@/js/stores/apiStore';

import formatPrice from '@/js/functions/formatPrice.js';
import { useCookieStore } from '@/js/stores/cookiesStore';
import Sizes from './partials/Sizes.vue';
import Contact from '@/js/components/Contact.vue';

const authStore = useAuthStore();

const apiStore = useApiStore();
const route = useRoute();
const accessory = ref({
    stock: {},
});
const selectedSize = ref('');

const addToFav = itemId => {
    apiStore
        .call('POST', '/api/favorites/updateFavorites', {
            itemId: itemId,
            type: 'accessory',
        })
        .then(res => {
            useCookieStore().setFavCount(res.favoriteCount);
        })
        .catch(error => {
            console.log(error);
        });
};

const userInfos = ref({
    firstname: authStore.$state.user?.firstname ?? '',
    lastname: authStore.$state.user?.lastname ?? '',
    companyName: authStore.$state.user?.company?.name ?? '',
    email: authStore.$state.user?.email ?? '',
    phone: authStore.$state.user?.phone ?? '',
    message: '',
});

await apiStore
    .apiGet(`/api/accessories-catalog/${route.params.id}/details`)
    .then(response => {
        accessory.value = response;
        if (accessory.value.sizes.length == 1) {
            selectedSize.value = accessory.value.sizes[0];
        }
        userInfos.value.message =
            "Bonjour,\n \n Je suis intéressé par essayer l'accessoire " +
            response.brand +
            ' ' +
            response.model +
            ' proposé au prix de ' +
            formatPrice(
                (response.price_tvac * (100 - response.discount)) / 100,
            );
    });
onBeforeUnmount(() => {
    document.body.style.overflow = 'auto';
});
</script>
